import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Alert,
  Checkbox,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  NotificationsActive as NotificationsActiveIcon,
} from '@mui/icons-material';
import { systemNotificationAPI, tenantAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

interface SystemNotification {
  id: string;
  title: string;
  message: string;
  type: 'informational' | 'warning' | 'emergency';
  startDate: string;
  tenantIds: string[];
  isActive: boolean;
  createdAt: string;
}

interface Tenant {
  id: string;
  name: string;
  identifier: string;
}

interface NotificationFormData {
  title: string;
  message: string;
  type: 'informational' | 'warning' | 'emergency';
  startDate: string;
  tenantIds: string[];
  isActive: boolean;
}

const emptyForm: NotificationFormData = {
  title: '',
  message: '',
  type: 'informational',
  startDate: new Date().toISOString().split('T')[0],
  tenantIds: [],
  isActive: true,
};

const typeColors: Record<string, 'default' | 'info' | 'warning' | 'error'> = {
  informational: 'info',
  warning: 'warning',
  emergency: 'error',
};

const SystemNotificationsPage: React.FC = () => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<SystemNotification[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<NotificationFormData>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<SystemNotification | null>(null);
  const [error, setError] = useState<string | null>(null);

  const isSuperAdmin = user?.role === 'super_admin';

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [notifRes, tenantRes] = await Promise.all([
        systemNotificationAPI.getAll(),
        tenantAPI.getAllTenants(),
      ]);
      setNotifications(notifRes.data);
      setTenants(tenantRes.data);
    } catch (err) {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isSuperAdmin) fetchData();
  }, [isSuperAdmin, fetchData]);

  const handleOpenCreate = () => {
    setEditingId(null);
    setFormData(emptyForm);
    setDialogOpen(true);
  };

  const handleOpenEdit = (notification: SystemNotification) => {
    setEditingId(notification.id);
    setFormData({
      title: notification.title,
      message: notification.message,
      type: notification.type,
      startDate: notification.startDate,
      tenantIds: notification.tenantIds,
      isActive: notification.isActive,
    });
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingId(null);
    setFormData(emptyForm);
  };

  const handleSubmit = async () => {
    if (!formData.title.trim() || !formData.message.trim() || !formData.startDate) {
      setError('Title, message, and start date are required');
      return;
    }
    try {
      setSaving(true);
      setError(null);
      if (editingId) {
        await systemNotificationAPI.update(editingId, formData);
      } else {
        await systemNotificationAPI.create(formData);
      }
      await fetchData();
      handleCloseDialog();
    } catch (err) {
      setError('Failed to save notification');
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePrompt = (notification: SystemNotification) => {
    setDeleteTarget(notification);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await systemNotificationAPI.delete(deleteTarget.id);
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
      await fetchData();
    } catch (err) {
      setError('Failed to delete notification');
    }
  };

  const handleToggleActive = async (notification: SystemNotification) => {
    try {
      await systemNotificationAPI.update(notification.id, { isActive: !notification.isActive });
      setNotifications((prev) =>
        prev.map((n) => (n.id === notification.id ? { ...n, isActive: !n.isActive } : n)),
      );
    } catch (err) {
      setError('Failed to update notification status');
    }
  };

  const getTenantNames = (ids: string[]): string => {
    if (!ids || ids.length === 0) return 'All Tenants';
    return ids
      .map((id) => tenants.find((t) => t.id === id)?.name || id)
      .join(', ');
  };

  if (!isSuperAdmin) {
    return <Alert severity="error">Access denied</Alert>;
  }

  return (
    <Box>
      <Paper sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box display="flex" alignItems="center" gap={1}>
            <NotificationsActiveIcon color="primary" />
            <Typography variant="h5">System Notifications</Typography>
          </Box>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate}>
            New Notification
          </Button>
        </Box>

        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Title</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Start Date</TableCell>
                  <TableCell>Target Tenants</TableCell>
                  <TableCell>Active</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {notifications.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography color="text.secondary" py={2}>
                        No notifications created yet
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  notifications.map((n) => (
                    <TableRow key={n.id}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {n.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 300, display: 'block' }}>
                          {n.message}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={n.type.charAt(0).toUpperCase() + n.type.slice(1)}
                          color={typeColors[n.type]}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{n.startDate}</TableCell>
                      <TableCell>
                        <Typography variant="body2">{getTenantNames(n.tenantIds)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={n.isActive ? 'Deactivate' : 'Activate'}>
                          <Switch
                            checked={n.isActive}
                            onChange={() => handleToggleActive(n)}
                            size="small"
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => handleOpenEdit(n)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error" onClick={() => handleDeletePrompt(n)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId ? 'Edit Notification' : 'New System Notification'}</DialogTitle>
        <DialogContent dividers>
          <Box display="flex" flexDirection="column" gap={2} pt={1}>
            {error && (
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            <TextField
              label="Title"
              value={formData.title}
              onChange={(e) => setFormData((f) => ({ ...f, title: e.target.value }))}
              fullWidth
              required
              inputProps={{ maxLength: 255 }}
            />

            <TextField
              label="Message"
              value={formData.message}
              onChange={(e) => setFormData((f) => ({ ...f, message: e.target.value }))}
              fullWidth
              required
              multiline
              minRows={3}
            />

            <FormControl fullWidth required>
              <InputLabel>Type</InputLabel>
              <Select
                value={formData.type}
                label="Type"
                onChange={(e) =>
                  setFormData((f) => ({ ...f, type: e.target.value as NotificationFormData['type'] }))
                }
              >
                <MenuItem value="informational">Informational</MenuItem>
                <MenuItem value="warning">Warning</MenuItem>
                <MenuItem value="emergency">Emergency</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Start Date"
              type="date"
              value={formData.startDate}
              onChange={(e) => setFormData((f) => ({ ...f, startDate: e.target.value }))}
              fullWidth
              required
              InputLabelProps={{ shrink: true }}
            />

            <FormControl fullWidth>
              <InputLabel>Target Tenants</InputLabel>
              <Select<string[]>
                multiple
                value={formData.tenantIds}
                onChange={(e) => {
                  const val = e.target.value;
                  setFormData((f) => ({
                    ...f,
                    tenantIds: typeof val === 'string' ? val.split(',') : (val as string[]),
                  }));
                }}
                input={<OutlinedInput label="Target Tenants" />}
                renderValue={(selected) =>
                  (selected as string[]).length === 0
                    ? 'All Tenants'
                    : (selected as string[])
                        .map((id) => tenants.find((t) => t.id === id)?.name || id)
                        .join(', ')
                }
                displayEmpty
              >
                {tenants.map((tenant) => (
                  <MenuItem key={tenant.id} value={tenant.id}>
                    <Checkbox checked={formData.tenantIds.includes(tenant.id)} />
                    <ListItemText primary={tenant.name} secondary={tenant.identifier} />
                  </MenuItem>
                ))}
              </Select>
              <Typography variant="caption" color="text.secondary" mt={0.5}>
                Leave empty to target all tenants
              </Typography>
            </FormControl>

            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => setFormData((f) => ({ ...f, isActive: e.target.checked }))}
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} /> : undefined}
          >
            {editingId ? 'Save Changes' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs">
        <DialogTitle>Delete Notification</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{deleteTarget?.title}</strong>? This cannot be
            undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleConfirmDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SystemNotificationsPage;
